import { useState, useEffect } from "react";

export type UseLocalStorageOptions = {
  reviver?: (key: string, value: unknown) => unknown;
  replacer?: (key: string, value: unknown) => unknown;
};

export const defaultDateReviver = (_key: string, value: unknown) => {
  if (typeof value === "string") {
    // convert ISO-date
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  return value;
};

export const useLocalStorageValue = <T>(
  key: string,
  defaultValue: T,
  userId?: string,
  options?: UseLocalStorageOptions
) => {
  const { reviver, replacer } = options || {};
  const internalKey = userId ? `${key}_${userId}` : key;
  const [value, setValue] = useState<T>(() => {
    const stored = localStorage.getItem(internalKey);
    return stored ? (JSON.parse(stored, reviver) as T) : defaultValue;
  });

  useEffect(() => {
    try {
      const serialized = JSON.stringify(value, replacer);
      localStorage.setItem(internalKey, serialized);
    } catch {
      // no-op
    }
  }, [internalKey, value, replacer]);

  const resetValue = () => {
    setValue(defaultValue);
  };

  return { value, setValue, resetValue } as const;
};
